import { RequestDocumentHistory } from '@/services/healthClaim/useRequestDocumentList/types'
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useClaimDocumentDownload } from '@/services/healthClaim/useClaimDocumentDownload'
import { useClaimDocumentUpload } from '@/services/healthClaim/useClaimDocumentUpload'
import message from '@/libs/message'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import {
  DOCUMENT_TYPE,
  HealthClaimModeEnum,
  MAX_CLAIM_DOCUMENT_SIZE_BYTES,
} from '@/constant/E_CLAIM'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'
import { DocumentDispatchContext } from '../HealthClaimDetailPage'
import { useConfirmReceiveDocument } from '@/services/healthClaim/useConfirmReceiveDocument'
import useCancelRequestDocument from '@/services/healthClaim/useCancelRequestDocument'
import {
  ClaimStatusCodeEnum,
  ReceivedDocumentStatusEnum,
} from '../../HealthClaimPage/ClaimHandlingTable/types'
import { useQueryClient } from '@tanstack/react-query'
import hasUpdatedDocumentStatusAtom from '@/recoil/hasUpdatedDocumentStatus/atom'
import userAtom from '@/recoil/user/atom'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'

const SupportingDocumentsForClaimHook = () => {
  const queryClient = useQueryClient()
  const params = useParams()
  const { claimFormId } = params
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [currentActiveTab, setCurrentActiveTab] = useState<string>('1')
  const [selectedRecord, setSelectedRecord] = useState<RequestDocumentHistory | null>(null)
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const setHasUpdatedDocumentStatus = useSetRecoilState(hasUpdatedDocumentStatusAtom)
  const isCreateOrEditMode =
    healthClaim.mode === HealthClaimModeEnum.CREATE || healthClaim.mode === HealthClaimModeEnum.EDIT
  const isEnableDownloadAll = healthClaim.data?.claimDocuments?.length > 0 && !isCreateOrEditMode
  const isDocumentsRequestedStatus =
    healthClaim.data?.statusCode === ClaimStatusCodeEnum.DOCUMENTS_REQUESTED
  const receivedDocumentStatus = healthClaim.data?.receivedDocumentStatus

  const [isConfirmReceiveModalOpen, setIsConfirmReceiveModalOpen] = useState(false)
  const [isCancelRequestModalOpen, setIsCancelRequestModalOpen] = useState(false)
  const { mutateAsync: downloadAll, isPending } = useClaimDocumentDownload()
  const { mutateAsync: confirmReceiveDocument, isPending: isConfirmReceivePending } =
    useConfirmReceiveDocument()
  const { mutateAsync: cancelRequestDocument, isPending: isCancelRequestPending } =
    useCancelRequestDocument()
  const user = useRecoilValue(userAtom)
  const canReceivedDocument = user.permissions.includes(BACKOFFICE_USER_PERMISSION.CLM010106)

  const [bulkUploadProgress, setBulkUploadProgress] = useState(0)
  const { mutateAsync: uploadBulkDocument, isPending: isBulkUploadPending } =
    useClaimDocumentUpload(DOCUMENT_TYPE.OTHER, setBulkUploadProgress)
  const dispatch = useContext(DocumentDispatchContext)
  const { markEditedOnce } = useHealthClaimForm()
  const bulkFileInputRef = useRef<HTMLInputElement>(null)

  const triggerBulkFilePicker = () => {
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = ''
    }
    bulkFileInputRef.current?.click()
  }

  const handleBulkFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.size > MAX_CLAIM_DOCUMENT_SIZE_BYTES) {
        message.error('ไฟล์มีขนาดเกิน 500MB กรุณาเลือกไฟล์ที่มีขนาดเล็กกว่า', 10)
        return
      }
      try {
        setBulkUploadProgress(0)
        const response = await uploadBulkDocument(file)
        dispatch({
          type: 'SET_UPLOADING',
          code: DOCUMENT_TYPE.OTHER,
          payload: { ...response, mimeType: response.mimeType ?? file.type },
        })
        markEditedOnce()
        message.success('อัปโหลดเอกสารสำเร็จ')
      } catch {
        message.error('อัปโหลดเอกสารไม่สำเร็จ')
      }
    }
  }

  const statusDisplayReceivedTabs = [
    ReceivedDocumentStatusEnum.RECEIVED,
    ReceivedDocumentStatusEnum.NOT_RECEIVED,
  ]
  const handleDownLoadAll = async () => {
    if (!claimFormId) return
    try {
      const { blob, filename } = await downloadAll({ claimId: claimFormId })

      // สร้างลิงก์ดาวน์โหลดจาก Blob
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      message.error(error?.message || 'ดาวน์โหลดไม่สำเร็จ')
    }
  }

  const handleClickDocument = () => {
    setIsModalOpen(true)
  }

  const handleModalOk = () => {
    setIsModalOpen(false)
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
    setSelectedRecord(null)
  }

  const handleTabClick = (key: string) => {
    setCurrentActiveTab(key)
  }

  const handleOpenModalWithRecord = (record: RequestDocumentHistory) => {
    setIsModalOpen(true)
    setSelectedRecord(record)
  }
  const handleConfirmReceive = () => {
    setIsConfirmReceiveModalOpen(true)
  }
  const handleConfirmReceiveModalClose = () => {
    setIsConfirmReceiveModalOpen(false)
  }

  const handleRefetchQueries = () => {
    // Ensure detail is fresh when navigating
    queryClient.refetchQueries({
      queryKey: ['useGetClaimDetail', healthClaim.data?.claimFormId],
    })
    queryClient.refetchQueries({
      queryKey: ['useOriginalDocumentList', healthClaim.data?.claimFormId],
    })
    queryClient.refetchQueries({
      queryKey: ['useRequestDocumentList', healthClaim.data?.claimFormId],
    })
  }

  const handleConfirmReceiveModalConfirm = async () => {
    try {
      await confirmReceiveDocument(claimFormId).then(() => {
        handleRefetchQueries()
      })

      setCurrentActiveTab('3')
      setHasUpdatedDocumentStatus(true)
      if (healthClaim?.data?.statusCode === ClaimStatusCodeEnum.DOCUMENTS_REQUESTED) {
        setIsCancelRequestModalOpen(true)
      }
    } catch (error) {
      message.error(error?.message || 'ยืนยันการรับเอกสารฉบับจริงไม่สำเร็จ')
    } finally {
      setIsConfirmReceiveModalOpen(false)
    }
  }

  useEffect(() => {
    if (receivedDocumentStatus === ReceivedDocumentStatusEnum.NOT_USE) {
      setCurrentActiveTab('1')
    }
  }, [receivedDocumentStatus])

  const handleCancelRequestModalClose = () => {
    setIsCancelRequestModalOpen(false)
  }

  const handleCancelRequestModalConfirm = async (requestDocumentHistoryId) => {
    try {
      await cancelRequestDocument({ claimFormId, requestDocumentHistoryId }).then(() => {
        handleRefetchQueries()
      })
    } catch (error) {
      message.error(error?.message || 'ยกเลิกการขอเอกสารเพิ่มเติมไม่สำเร็จ')
    } finally {
      setIsCancelRequestModalOpen(false)
    }
  }

  return {
    isModalOpen,
    handleClickDocument,
    handleModalOk,
    handleModalCancel,
    handleOpenModalWithRecord,
    selectedRecord,
    isPending,
    handleDownLoadAll,
    isCreateOrEditMode,
    isEnableDownloadAll,
    isDocumentsRequestedStatus,
    isConfirmReceiveModalOpen,
    isConfirmReceivePending,
    handleConfirmReceive,
    handleConfirmReceiveModalClose,
    handleConfirmReceiveModalConfirm,
    isCancelRequestModalOpen,
    isCancelRequestPending,
    handleCancelRequestModalClose,
    handleCancelRequestModalConfirm,
    receivedDocumentStatus,
    statusDisplayReceivedTabs,
    currentActiveTab,
    handleTabClick,
    canReceivedDocument,
    triggerBulkFilePicker,
    handleBulkFileChange,
    bulkFileInputRef,
    isBulkUploadPending,
    bulkUploadProgress,
  }
}

export default SupportingDocumentsForClaimHook
